import {Injectable} from '@angular/core';
import {IParticipant, IParticipantConfig} from '@interfaces/interfaces';
import {ParticipantRestService} from '../rest/participant-rest.service';
import {ProjectDto} from '@api/projects/interfaces';
import {ProjectService} from '../project/project.service';
import {ProjectRestService} from '../rest/project-rest.service';
import {filter, shareReplay, switchMap, tap} from 'rxjs/operators';
import { JSONSchema7 } from 'json-schema';
import {Resolver} from '@stoplight/json-ref-resolver';
import {BehaviorSubject, Observable} from 'rxjs';
import {TParticipantSearchResult} from '@app/services/participants/interfaces';
import { PermissionService } from '@app/services/permission/permission.service';

@Injectable({
	providedIn: 'root'
})
export class ParticipantService {
	private _participantsOfActiveProject$: Observable<IParticipant[]>;
	private _participantsConfigOfActiveProject$: Observable<IParticipantConfig>;
	private _resolvedSchema$: Observable<JSONSchema7>;

	private _participantsConfigOfActiveProject: IParticipantConfig = null;
	private _tempParticipantsPipe$: BehaviorSubject<IParticipant[]>;
	private activeProjectId: string;
	private _newParticipant$: BehaviorSubject<IParticipant>;

	private newParticipantCountdown: number;

	constructor(
		private participantRestService: ParticipantRestService,
		private projectRestService: ProjectRestService,
		private projectService: ProjectService,
		permissionService: PermissionService,
	) {
		this._tempParticipantsPipe$ = new BehaviorSubject([]);
		this._newParticipant$ = new BehaviorSubject(null);

		this.projectService.activeProject$.subscribe(activeProject => this.activeProjectId = activeProject.project_id);

		this._participantsOfActiveProject$ = this.projectService.activeProject$.pipe(
			switchMap(() => this._refreshParticipants()),
		);

		this._participantsConfigOfActiveProject$ = this.projectService.activeProject$.pipe(
			permissionService.checkPermission('participant-config:read'),
			switchMap(project => this.loadParticipantsConfigOfActiveProject(project)),
			shareReplay({ bufferSize: 1, refCount: false }),
		);

		this._resolvedSchema$ = this._participantsConfigOfActiveProject$.pipe(
			switchMap(config => this.resolveSchema(config)),
			shareReplay({ bufferSize: 1, refCount: false }),
		);

		this._participantsConfigOfActiveProject$.subscribe(config => this._participantsConfigOfActiveProject = config );
	}

	private async loadParticipantsConfigOfActiveProject(project: ProjectDto): Promise<IParticipantConfig> {
		return project && await this.projectRestService.getParticipantsConfig$(project.project_id).toPromise();
	}

	public getParticipant$(projectId: string, participantId: string): Observable<IParticipant> {
		return this.participantRestService.getParticipant$(projectId, participantId);
	}

	private _refreshParticipants(): Observable<IParticipant[]> {
		const projectID = this.activeProjectId;
		this.participantRestService
			.listParticipants$(projectID)
			.pipe(
				filter(() => this.activeProjectId === projectID),
				tap(newParticipants => this._tempParticipantsPipe$.next(newParticipants)),
			).subscribe();

		return this._tempParticipantsPipe$;
	}

	public get participantsOfActiveProject$(): Observable<IParticipant[]> {
		return this._participantsOfActiveProject$;
	}

	public get participantsConfigOfActiveProject(): IParticipantConfig {
		return this._participantsConfigOfActiveProject;
	}

	public get resolvedParticipantsConfigSchema$(): Observable<JSONSchema7> {
		return this._resolvedSchema$;
	}

	private async resolveSchema(config: IParticipantConfig): Promise<JSONSchema7> {
		if (config?.schema) {
			const resolver = new Resolver();
			const resolved = await resolver.resolve(config.schema);
			return resolved.result;
		}
		return undefined;
	}

	public createNewParticipant(project: ProjectDto, participant: IParticipant): Promise<IParticipant> {
		return this.participantRestService.createParticipant(project.project_id, participant);
	}

	public updateParticipant(project: ProjectDto, participant: IParticipant): Promise<IParticipant> {
		return this.participantRestService.updateParticipant(project.project_id, participant);
	}

	public deleteParticipant(project: ProjectDto, participant: IParticipant): Promise<void> {
		return this.participantRestService.deleteParticipant(project.project_id, participant);
	}

	public refreshParticipants(): void {
		this._refreshParticipants();
	}

	public searchMetaByString(search: string, isProtectedSearch: boolean = false): Promise<TParticipantSearchResult> {
		return this.participantRestService.searchAll(this.activeProjectId, search || '', isProtectedSearch)
			.toPromise()
			.catch(
				err => {
					if (err.status === 403) {
						return 'unspecific';
					}
					return null;
				}
			)
			.then(
				(data: IParticipant[] | 'unspecific' | null) => {
					if (data === 'unspecific') {
						return {
							status: 'unspecific',
							results: [],
						};
					}
					if (data === null) {
						return {
							status: 'invalid',
							results: [],
						};
					}
					return {
						status: 'valid',
						results: data,
					};
				}
			);
	}

}
